<template>
    <div>
        <el-breadcrumb style="float: right" separator-class="el-icon-arrow-right">
            <el-breadcrumb-item :to="{ path: '/' }">{{$t('route.home')}}</el-breadcrumb-item>
            <el-breadcrumb-item>{{$t('entities.club.plural')}}</el-breadcrumb-item>
        </el-breadcrumb>

        <h2 class="page-title">{{$t('entities.club.plural')}}</h2>

        <el-row :gutter="20">
            <el-col :xs="24" :md="8">
                <el-form @submit.native.prevent class="search" :inline="true">
                    <el-form-item>
                        <el-input v-model="pager.search" :placeholder="$t('table.search')">
                            <el-button slot="append" icon="el-icon-search"></el-button>
                        </el-input>
                    </el-form-item>
                </el-form>
            </el-col>

            <el-col class="button-group" :xs="24" :md="16">
                <el-button
                    v-if="hasRoleToCreate"
                    icon="el-icon-fa-file-o"
                    @click="create()"
                    type="primary"
                >{{$t('table.add')}}</el-button>
                <el-button
                    v-if="hasRoleToReadLogs"
                    icon="el-icon-fa-history"
                    @click="history()"
                    type="infor"
                >{{$t('table.history')}}</el-button>

                <el-button
                    v-if="hasRoleToCreate && hasRoleToReadLogs"
                    @click="importCsv()"
                    :plain="true"
                    type="success"
                    icon="el-icon-fa-table"
                >{{$t('table.import')}}</el-button>

                <el-button
                    v-if="pager.hasDataToExport(list) && hasRoleToReadLogs"
                    @click="pager.exportToCsv(list)"
                    :plain="true"
                    type="primary"
                    icon="el-icon-fa-table"
                >{{$t('table.export')}}</el-button>
            </el-col>
        </el-row>
        <!-- ************************** -->
        <!-- *                        * -->
        <!-- *  Club import data      * -->
        <!-- *                        * -->
        <!-- ************************** -->
        <transition name="fade" mode="out-in">
            <router-view name="clubImport"></router-view>
        </transition>

        <el-table
            :data="pager.paginate(list)"
            :default-sort="{
                prop: 'name',
                order: 'ascending'
            }"
            @row-click="rowClick"
            empty-text="There are no clubs yet"
            @sort-change="(args) => pager.sortChange(args)"
            style="width: 100%"
            v-loading="loading"
            border
        >
            <el-table-column prop="name" :label="$t('entities.club.prop.name')" sortable="custom"></el-table-column>

            <el-table-column
                v-if="hasRoleToUpdate"
                prop="emailManager"
                :label="$t('entities.club.prop.emailManager')"
                sortable="custom"
            ></el-table-column>

            <el-table-column
                v-if="hasRoleToReadLogs"
                prop="modified"
                :label="$t('status.modifiedAt')"
                sortable="custom"
            >
                <template slot-scope="scope">{{ scope.row.modified | formatDatetime }}</template>
            </el-table-column>

            <el-table-column
                v-if="hasRoleToReadLogs"
                prop="created"
                :label="$t('status.created')"
                sortable="custom"
            >
                <template slot-scope="scope">{{ scope.row.created | formatDatetime }}</template>
            </el-table-column>

            <el-table-column
                v-if="hasRoleToUpdate"
                prop="statusManager"
                :label="$t('entities.club.prop.statusManager')"
                sortable="custom"
            >
                <!-- :filters="statusManagerList" 
                :filter-method="filterByStatusManager"-->
                <template slot-scope="scope">
                    <el-tag
                        :type="getSyleStatusManager(scope.row.statusManager)"
                        disable-transitions
                    >{{ scope.row.statusManagerEs }}</el-tag>
                </template>
            </el-table-column>

            <!-- Size teams -->
            <el-table-column
                prop="sizeTeams"
                :label="$t('entities.team.numberOfTeams')"
                sortable="custom"
            >
                <template slot-scope="scope">{{ getNumTeams(scope.row.teams) }}</template>
            </el-table-column>

            <!-- Size players -->
            <el-table-column
                prop="sizePlayers"
                :label="$t('entities.player.numberOfPlayers')"
                sortable="custom"
            >
                <template slot-scope="scope">{{ getNumPlayers(scope.row.teams) }}</template>
            </el-table-column>
            <!-- Size coaches -->
            <el-table-column
                prop="sizePlayers"
                :label="$t('entities.coach.numberOfCoaches')"
                sortable="custom"
            >
                <template slot-scope="scope">{{ getNumCoaches(scope.row.teams) }}</template>
            </el-table-column>
            <!-- Size team managers -->
            <el-table-column
                prop="sizePlayers"
                :label="$t('entities.teamManager.numberOfTeamManager')"
                sortable="custom"
            >
                <template slot-scope="scope">{{ getNumTeamManagers(scope.row.teams) }}</template>
            </el-table-column>
            
            <!-- ************************** -->
            <!-- *                        * -->
            <!-- *         Actions        * -->
            <!-- *                        * -->
            <!-- ************************** -->
            <el-table-column
                :fixed="isMobile? undefined : 'right'"
                align="center"
                :label="$t('table.actions')"
                width="160"
            >
                <template slot-scope="scope">
                    <el-tooltip
                        v-if="hasRoleToUpdate && scope.row.statusManager != 'verified'"
                        :content="$t('table.sendInvitation')"
                        placement="top"
                    >
                        <el-button
                            icon="el-icon-fa-bell"
                            size="small"
                            type="text"
                            @click="sendInvitation(scope.row)"
                        />
                    </el-tooltip>

                    <el-tooltip v-if="hasRoleToRead" :content="$t('table.details')" placement="top">
                        <el-button
                            icon="el-icon-fa-search"
                            size="small"
                            type="text"
                            @click="show(scope.row.id)"
                        />
                    </el-tooltip>

                    <el-tooltip v-if="hasRoleToUpdate" :content="$t('table.edit')" placement="top">
                        <el-button
                            icon="el-icon-fa-pencil-square-o"
                            size="small"
                            type="text"
                            @click="edit(scope.row.id)"
                        />
                    </el-tooltip>

                    <el-tooltip
                        v-if="hasRoleToDelete"
                        :content="$t('table.delete')"
                        placement="top"
                    >
                        <el-button
                            icon="el-icon-fa-trash"
                            size="small"
                            type="text"
                            @click="destroy(scope.row)"
                        />
                    </el-tooltip>
                </template>
            </el-table-column>
        </el-table>

        <div class="mt-3">
            <el-pagination
                style="float: right"
                @size-change="(args) => pager.pageSizeChange(args)"
                :current-page.sync="pager.currentPage"
                :layout="paginationLayout"
                :total="pager.filteredAndSortedList(list).length"
                background
            ></el-pagination>
        </div>
    </div>
</template>

<script>
/* eslint-disable */
import { mapGetters } from "vuex";
import _ from "underscore";

export default {
    name: "app-club-import",

    data() {
        return {
            statusManagerList: [
                { text: "No invitado", value: "unverified" },
                { text: "Invitado", value: "invited" },
                { text: "Registrado", value: "verified" }
            ]
        };
    },
    computed: {
        ...mapGetters({
            loading: "entities/club/loading",
            list: "entities/club/list",
            pager: "entities/club/pager",
            hasRoleToDelete: "entities/club/hasRoleToDelete",
            hasRoleToUpdate: "entities/club/hasRoleToUpdate",
            hasRoleToRead: "entities/club/hasRoleToRead",
            hasRoleToReadLogs: "entities/club/hasRoleToReadLogs",
            hasRoleToCreate: "entities/club/hasRoleToCreate",
            isClubManager: "entities/club/isClubManager",
            isMobile: "shared/isMobile",
            paginationLayout: "shared/paginationLayout"
        })
    },

    filters: {
        capitalize: function(str) {
            return str.charAt(0).toUpperCase() + str.slice(1);
        }
    },

    created() {
        if (this.isClubManager) {
            this.$store.dispatch("entities/club/listClubManager");
        } else {
            this.$store.dispatch("entities/club/list");
        }
    },

    beforeRouteLeave(to, from, next) {
        if (to.name !== "clubImportCsv") {
            this.$store.dispatch("entities/club/clear");
        }
        next();
    },

    beforeRouteUpdate(to, from, next) {
        if (to.name !== "clubImportCsv") {
            if (this.isClubManager) {
                this.$store.dispatch("entities/club/listClubManager");
            } else {
                this.$store.dispatch("entities/club/list");
            }
        }
        next();
    },

    methods: {
        create() {
            this.$router.push({
                name: "clubNew"
            });
        },

        rowClick(row, column, cell, event) {
            if (cell.label != this.$t("table.actions")) {
                this.$router.push({
                    name: "clubShow",
                    params: { id: row.id }
                });
            }
        },

        sendInvitation(club) {
            this.$store
                .dispatch("entities/club/createManagerSendEmail", club)
                .then(() => {
                    if (this.isClubManager) {
                        this.$store.dispatch("entities/club/listClubManager");
                    } else {
                        this.$store.dispatch("entities/club/list");
                    }
                });
        },

        show(id) {
            this.$router.push({
                name: "clubShow",
                params: { id }
            });
        },

        edit(id) {
            this.$router.push({
                name: "clubEdit",
                params: { id }
            });
        },

        importCsv() {
            this.$router.push({
                name: "clubImportCsv"
            });
        },

        destroy(model) {
            this.$confirm(
                this.$t("table.areYouSure"),
                this.$t("table.confirm"),
                {
                    confirmButtonText: this.$t("table.yes"),
                    cancelButtonText: this.$t("table.no"),
                    type: "warning"
                }
            )
                .then(() =>
                    this.$store.dispatch("entities/club/destroy", model)
                )
                .catch(() => {});
        },

        history() {
            this.$router.push({ name: "clubHistoryFull" });
        },

        filterByStatusManager(value, row) {
            return row.statusManager === value;
        },

        // Aux UI functions
        getSyleStatusManager(statusManager) {
            if (statusManager == "unverified") {
                return "danger";
            } else if (statusManager == "invited") {
                return "warning";
            } else if (statusManager == "verified") {
                return "success";
            }
        },
        getLabelStatusManager(statusManager) {
            if (statusManager == "unverified") {
                return "No invitado";
            } else if (statusManager == "invited") {
                return "Invitado";
            } else if (statusManager == "verified") {
                return "Registrado";
            }
        },

        getNumTeams(teams) {
            return _.size(teams);
        },

        getNumPlayers(teams) {
            var num = 0;

            _.forEach(teams, team => {
                num += _.size(team.players);
            });

            return num;
        },
        getNumCoaches(teams) {
            var num = 0;

            _.forEach(teams, team => {
                num += _.size(team.coaches);
            });

            return num;
        },
        getNumTeamManagers(teams) {
            var num = 0;

            _.forEach(teams, team => {
                num += _.size(team.teamManagers);
            });

            return num;
        }
    }
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/entities/club/club-list.vue?2fde2ada